import {Text} from '@/components-v2/atoms/Text/Text'
import {Toggle} from '@/components-v2/atoms/Toggle/Toggle'
import {Notification} from '@/components-v2/molecules/Notification/Notification'
import {Modal} from '@/components-v2/organisms/Modal/Modal'
import {move} from '@dnd-kit/helpers'
import {DragDropProvider} from '@dnd-kit/react'
import {useSortable} from '@dnd-kit/react/sortable'
import {Icon} from '@pepsico-ds/ui'
import {useEffect, useState} from 'react'

const MAX_VISIBLE_COLUMNS = 20

/**
 * Merges the full column list with the currently applied (ordered) visible keys: visible
 * columns come first, in their applied order, followed by the remaining hidden columns.
 *
 * @param {Array<{columnKey: string, header: string}>} columns
 * @param {string[]} visibleColumnKeys
 */
const buildOrderedColumns = (columns, visibleColumnKeys) => {
  const byKey = new Map(columns.map(col => [col.columnKey, col]))
  const visibleSet = new Set(visibleColumnKeys)
  const visible = visibleColumnKeys.map(key => byKey.get(key)).filter(Boolean)
  const hidden = columns.filter(col => !visibleSet.has(col.columnKey))

  return [...visible, ...hidden].map(col => ({...col, checked: visibleSet.has(col.columnKey)}))
}

const ColumnRow = ({column, index, onToggle, disableEnable}) => {
  const {ref, handleRef, isDragging} = useSortable({id: column.columnKey, index})

  return (
    <div
      ref={ref}
      className={`flex items-center gap-3 border-b border-[var(--color-border-default)] py-2 ${isDragging ? 'opacity-50' : ''}`}
    >
      <button ref={handleRef} type="button" className="flex cursor-grab touch-none" aria-label="Drag to reorder">
        <Icon icon="drag_indicator" size="small" className="text-[var(--color-text-primary)]" />
      </button>
      <Text className="flex-1" size="small">
        {column.header}
      </Text>
      <Toggle
        size="small"
        checked={column.checked}
        disabled={disableEnable && !column.checked}
        onUpdate={checked => onToggle(column.columnKey, checked)}
      />
    </div>
  )
}

/**
 * Modal for toggling and reordering product list columns. Selection/order only takes effect
 * (and is only reported via onApply) once the user clicks Apply.
 *
 * @param {{
 *   showModal: boolean,
 *   onCloseModal: () => void,
 *   columns: Array<{columnKey: string, header: string}>,
 *   visibleColumnKeys: string[],
 *   onApply: (keys: string[]) => void,
 * }} props
 */
export const ColumnSettingsModal = ({showModal, onCloseModal, columns, visibleColumnKeys, onApply}) => {
  const [orderedColumns, setOrderedColumns] = useState(() => buildOrderedColumns(columns, visibleColumnKeys))
  const selectedColumnCount = orderedColumns.filter(column => column.checked).length
  const hasReachedColumnLimit = selectedColumnCount >= MAX_VISIBLE_COLUMNS

  // Re-sync local order/selection with the applied one each time the modal is (re)opened.
  useEffect(() => {
    if (showModal) {
      setOrderedColumns(buildOrderedColumns(columns, visibleColumnKeys))
    }
  }, [showModal, columns, visibleColumnKeys])

  const handleDragEnd = event => {
    setOrderedColumns(prev => move(prev, event))
  }

  const handleToggle = (columnKey, checked) => {
    if (checked && hasReachedColumnLimit) {
      return
    }

    setOrderedColumns(prev => prev.map(col => (col.columnKey === columnKey ? {...col, checked} : col)))
  }

  const handleApply = () => {
    onApply(orderedColumns.filter(col => col.checked).map(col => col.columnKey))
    onCloseModal()
  }

  return (
    <Modal
      showModal={showModal}
      onCloseModal={onCloseModal}
      title="Table Customization"
      text="Use the toggles below to customize which columns are displayed in your table. Some columns are required by default and cannot be removed."
      type="fluid"
      showSecondaryButton
      secondaryButtonProps={{text: 'Cancel', variant: 'secondary', onClick: onCloseModal}}
      showTertiaryButton={false}
      primaryButtonProps={{text: 'Apply', onClick: handleApply}}
    >
      {hasReachedColumnLimit && (
        <Notification
          type="warning"
          text={`A maximum of ${MAX_VISIBLE_COLUMNS} columns can be displayed.`}
          showText
          showActionButtons={false}
        />
      )}
      <DragDropProvider onDragEnd={handleDragEnd}>
        <div className="flex max-h-[60vh] flex-col gap-2 overflow-y-auto p-5">
          {orderedColumns.map((column, index) => (
            <ColumnRow
              key={column.columnKey}
              column={column}
              index={index}
              onToggle={handleToggle}
              disableEnable={hasReachedColumnLimit}
            />
          ))}
        </div>
      </DragDropProvider>
    </Modal>
  )
}
